import { captureException } from '@sentry/react';
import chroma from 'chroma-js';
import { format } from 'd3-format';
import { geoPath } from 'd3-geo';
import { useState } from 'react';
import { feature } from 'topojson-client';

import { IContext } from './context';
import { POLICIES } from './data/policies';
import { Policies, StateId, TaxData } from './types';

export const showSumOfPolicies = (county: Policies, context: IContext) => {
	return POLICIES.reduce((acc, policy) => {
		if (context[policy.id]) {
			if (county.salt == 24 && county.it == -873) {
				console.log(policy.shorthand, county[policy.shorthand]);
			}
			return (acc += county[policy.shorthand]);
		}
		return (acc += 0);
	}, 0);
};

export const allPoliciesSelected = (context: IContext) => {
	return POLICIES.every(policy => context[policy.id]);
};

export const colorize = (value: number, domain: number[]) => {
	return chroma
		.scale([
			'#5e4fa2',
			'#3288bd',
			'#66c2a5',
			'#abdda4',
			'#e6f598',
			'#fee08b',
			'#fdae61',
			'#f46d43',
			'#d53e4f',
			'#9e0142',
		])
		.domain(domain)(value);
};

const formatTypes = ['%', '$', ','] as const;
export const formatter = (
	number: number,
	type: (typeof formatTypes)[number],
) => {
	if (Number.isNaN(+number)) {
		throw new Error('Invalid number for formatting.');
	}
	if (!formatTypes.includes(type)) {
		throw new Error('Invalid formatting type.');
	}
	if (type === '%') {
		return format('.1%')(number);
	} else if (type === '$') {
		return new Intl.NumberFormat('en-US', {
			style: 'currency',
			currency: 'USD',
			maximumFractionDigits: 0,
		}).format(number);
	} else if (type === ',') {
		return format(',.0f')(number);
	}
};

export const leadingZeroFIPS = (id: StateId) => {
	return id < 10 ? '0' + id : id;
};

export const fetchData = async (path: string) => {
	try {
		const url = `data/${path}.json`;
		const response = await fetch(url);

		if (!response.ok) {
			throw new Error(`${response.status} ${response.statusText}`);
		}

		const json = await response.json();
		return json;
	} catch (e) {
		console.error(e);
	}
};

const getQuery = () => {
	if (typeof window !== 'undefined') {
		return new URLSearchParams(window.location.search);
	}
	return new URLSearchParams();
};

const getQueryStringValue = (key: string) => getQuery().get(key);

export const useQueryParams = (key: string, defaultVal: string) => {
	const [query, setQuery] = useState<string>(
		getQueryStringValue(key) || defaultVal,
	);

	const updateUrl = (newVal: number) => {
		setQuery(String(newVal));

		const query = getQuery();

		if (!Number.isNaN(newVal) && newVal !== 0) {
			query.set(key, String(newVal));
		} else {
			query.delete(key);
		}

		// This check is necessary if using the hook with Gatsby
		if (typeof window !== 'undefined') {
			const { protocol, pathname, host } = window.location;
			const newUrl = `${protocol}//${host}${pathname}?${query.toString()}`;
			window.history.pushState({}, '', newUrl);
		}
	};

	return [query, updateUrl];
};

interface DrawCountiesCanvas {
	data: TaxData;
	us?: ReturnType<typeof feature>;
	counties: ReturnType<typeof feature>;
	year: number;
	path: ReturnType<typeof geoPath>;
	canvas: HTMLCanvasElement | null;
	xScale: number;
	yScale: number;
	domain: number[];
	context: IContext;
}

export const drawCountiesCanvas = ({
	data,
	us,
	counties,
	year,
	path,
	canvas,
	xScale,
	yScale,
	domain,
	context,
}: DrawCountiesCanvas) => {
	if (!('features' in counties)) {
		captureException(
			new Error('No features in counties data', {
				cause: counties,
			}),
		);
		console.error('No features in counties data');
		return;
	}

	if (!canvas) {
		console.error('No canvas');
		return;
	}

	let drawingContext = canvas.getContext('2d');
	if (!drawingContext) {
		console.error('No drawing context');
		return;
	}
	path.context(drawingContext);
	if (us) {
		drawingContext.clip(path(us) as CanvasFillRule);
	}
	drawingContext.imageSmoothingEnabled = false;

	// reset canvas to blank to prevent bleeding through of old border colors
	drawingContext.fillStyle = 'white';
	drawingContext.fillRect(0, 0, xScale, yScale);

	for (const d of counties.features) {
		if (!d.properties) continue;
		const stateFips = +d.properties.state as StateId;
		const countyFips = +d.properties.county;
		if (data[stateFips]?.data[year]?.[countyFips]) {
			const countyData = data[stateFips].data[year][countyFips];

			const sumOfPolicies = showSumOfPolicies(countyData, context);

			drawingContext.beginPath();
			path(d);
			drawingContext.fillStyle = countyData
				? colorize(sumOfPolicies, domain).hex()
				: '#888';
			drawingContext.fill();
			drawingContext.strokeStyle = countyData
				? colorize(showSumOfPolicies(countyData, context), domain).hex()
				: '#888';
			drawingContext.lineWidth = 1;
			drawingContext.stroke();
			drawingContext.closePath();
		}
	}
};
